import { useEffect, useLayoutEffect } from 'react'
import { useLocation } from 'react-router'
import { onSessionLost } from '@/lib/http/vfsclient/session'
import { resetIfStorageCleared, useSessionStore } from '../store/sessionStore'

/** How often to re-check the clock while the tab sits open. */
const POLL_MS = 30_000

/**
 * Applies the inactivity TTL, and keeps it from firing on a citizen who is still
 * working.
 *
 * Nothing used to apply it at all. `isExpired()` was read inside RequireSession's
 * render and, when it answered true, the guard bounced the citizen to `/apply` —
 * where the resume selector, which cannot see the TTL, read the still-present
 * appRefNo and sent them back to the step they had just left. That is the infinite
 * route change. Expiring the session *for real* is what breaks it: an empty session
 * resolves to the ID-number screen, which is a terminal destination.
 *
 * Mounted once in AppRoutes — inside the router, so it sits alongside the guards and
 * its `set` lands in the same commit they read; outside any route and outside the
 * Suspense boundary, so it survives every navigation and the clock is watched even
 * on `/track` and the static pages.
 *
 * It also applies the SERVER's verdict. The backend owns the session cookie, and when
 * it stops recognising it — expired past a re-bootstrap, or re-bound by a second tab
 * to another application — the transport says so once (`onSessionLost`), and the
 * same transition the clock triggers runs here: the store empties, RequireSession
 * sends the citizen to `/apply`, and the ID-number screen explains why.
 *
 * It is also the only thing that calls `touch()`. That action existed from the start
 * and had no callers, which meant the TTL counted thirty minutes from the last DATA
 * WRITE rather than the last sign of life. An officer at the Entra sign-in screen — a
 * full-page departure to Microsoft and back — or a citizen working through the
 * address form could cross it without being idle for a moment, and lose eight steps
 * of §4.6/§4.7 data that no endpoint can read back.
 */
export function SessionExpiryWatcher() {
  const { pathname } = useLocation()

  /**
   * Layout rather than passive, and separate from the subscriptions below: on a cold
   * load carrying a stale session in sessionStorage, this has to win the race against
   * the first paint, or the citizen sees a step of a dead application flash up before
   * the guard ejects them from it.
   */
  useLayoutEffect(() => {
    // First, and in the layout phase, because this is the earliest point at which a
    // resurrected session can be caught: `<Navigate>` redirects run as passive
    // effects, so emptying the store here means the resume selector never gets to
    // read a session the browser has already thrown away.
    if (resetIfStorageCleared()) return
    useSessionStore.getState().expireIfStale()
  }, [])

  /**
   * A navigation is a sign of life, so it re-arms the TTL — but only after the clock
   * has been read. Checking first is what stops a citizen returning from a long
   * absence from silently extending a session that had already lapsed.
   */
  useEffect(() => {
    // Before anything reads or writes the session: if storage was emptied out from
    // under the tab, `touch()` below is exactly the write that would put it back.
    if (resetIfStorageCleared()) return

    const { expireIfStale, touch, appRefNo } = useSessionStore.getState()
    if (expireIfStale()) return
    if (appRefNo) touch()
  }, [pathname])

  useEffect(() => onSessionLost(() => useSessionStore.getState().expireNow()), [])

  useEffect(() => {
    // Reading the store fresh inside the handler, not through a subscription: this
    // component must not re-render on every session write.
    const check = () => {
      if (resetIfStorageCleared()) return

      const { expireIfStale, touch, appRefNo } = useSessionStore.getState()
      if (expireIfStale()) return
      if (appRefNo) touch()
    }

    // Nothing re-renders on its own when a deadline passes, so without the interval
    // an expired session is only noticed the next time something unrelated happens to
    // re-render a guard — which ejects the citizen mid-keystroke.
    const timer = setInterval(() => {
      if (resetIfStorageCleared()) return
      useSessionStore.getState().expireIfStale()
    }, POLL_MS)

    // `visibilitychange` covers the mobile case the interval cannot: a backgrounded
    // tab is throttled or frozen, so the check that matters is the one on the way back.
    const onVisibility = () => {
      if (document.visibilityState === 'visible') check()
    }

    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('focus', check)

    return () => {
      clearInterval(timer)
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('focus', check)
    }
  }, [])

  return null
}
